<script lang="ts">
  // Activity · Analysis: a light data sheet of what Xert saw in the ride: XSS by intensity,
  // focus, difficulty, fuel, and the power/MPA chart.
  import { XSS_COLORS, type ReportContext } from '../context'
  import { statsFor } from '../stats'
  import Badges from '../parts/Badges.svelte'
  import Brand from '../parts/Brand.svelte'
  import Diamonds from '../parts/Diamonds.svelte'
  import Fill from '../parts/Fill.svelte'
  import Frame from '../parts/Frame.svelte'
  import PowerChart from '../parts/PowerChart.svelte'
  import StatGrid from '../parts/StatGrid.svelte'
  import TitleBlock from '../parts/TitleBlock.svelte'

  export let ctx: ReportContext

  $: a = ctx.data.activity
  $: parts = [
    { key: 'xlss', label: 'Low', value: a.xlss ?? 0, color: XSS_COLORS.xlss },
    { key: 'xhss', label: 'High', value: a.xhss ?? 0, color: XSS_COLORS.xhss },
    { key: 'xpss', label: 'Peak', value: a.xpss ?? 0, color: XSS_COLORS.xpss },
  ]
  $: total = parts.reduce((s, p) => s + p.value, 0) || 1
  $: stats = statsFor(ctx, ['stat.distance', 'stat.time', 'stat.xep', 'stat.elevation', 'stat.power', 'stat.hr', 'stat.recovery'])
  $: sig = ctx.data.signature
  $: cardCount = [
    ctx.show('focus') && a.focus,
    ctx.show('difficulty') && a.difficulty_stars,
    ctx.show('fuel') && (a.carbs || a.fat),
    ctx.show('signature') && sig,
  ].filter(Boolean).length
  // Three cards sit in a row (four too, in a square); otherwise two per row.
  // Focus names vary in length ("Pursuiter" to "Breakaway Specialist"); shrink long ones to fit.
  $: focusFont = (name: string) => {
    const base = { portrait: 56, square: 46, landscape: 42 }[ctx.size]
    const fits = cardColumns >= 3 ? { portrait: 12, square: 11, landscape: 9 }[ctx.size] : 18
    return Math.round(Math.max(base * 0.6, Math.min(base, (base * fits) / name.length)))
  }
  $: cardColumns = cardCount === 3 || (ctx.size === 'square' && cardCount === 4) ? cardCount : 2
</script>

<Frame {ctx} light base="linear-gradient(170deg, #fbfaf6 0%, #eeeae1 100%)" photoFilter="grayscale(1) contrast(0.9)">
  <div class="xr-layout">
    <div class="xr-top">
      <span class="xr-eyebrow">Ride Analysis</span>
      <Brand light height={38} />
    </div>

    <div class="xr-title"><TitleBlock {ctx} size={ctx.size === 'square' ? 'md' : 'lg'} lines={2} /></div>

    {#if ctx.show('xss') && a.xss}
      <div class="xr-xss">
        <div class="xr-xss-total">
          <span class="xr-xss-num">{Math.round(a.xss)}</span>
          <span class="xr-xss-label">XSS</span>
        </div>
        <div class="xr-xss-break">
          <div class="xr-bar">
            {#each parts as p}
              {#if p.value > 0}<div style="flex: {p.value}; background: {p.color};" />{/if}
            {/each}
          </div>
          <div class="xr-legend">
            {#each parts as p}
              <span><i style="background: {p.color};" />{p.label} <strong>{Math.round(p.value)}</strong> <em>{Math.round((p.value / total) * 100)}%</em></span>
            {/each}
          </div>
        </div>
      </div>
    {/if}

    <div class="xr-cards" style="grid-template-columns: repeat({cardColumns}, minmax(0, 1fr));">
      {#if ctx.show('focus') && a.focus}
        <div class="xr-card">
          <span class="xr-k">Focus</span>
          <span class="xr-v" style="font-size: {focusFont(a.focus)}px;">{a.focus}</span>
          <span class="xr-s">{ctx.fmt.focusDuration(a.focus_duration) ?? ''}{a.specificity ? ` · ${a.specificity}` : ''}</span>
        </div>
      {/if}
      {#if ctx.show('difficulty') && a.difficulty_stars}
        <div class="xr-card">
          <span class="xr-k">Difficulty</span>
          <span class="xr-v"><Diamonds value={a.difficulty_stars} size={ctx.size === 'landscape' ? 26 : 34} color="var(--accent)" /></span>
          <span class="xr-s">{a.difficulty_label ?? ''} · {Math.round(a.difficulty ?? 0)}</span>
        </div>
      {/if}
      {#if ctx.show('fuel') && (a.carbs || a.fat)}
        <div class="xr-card">
          <span class="xr-k">{ctx.size === 'landscape' ? 'Fuel' : 'Fuel Burned'}</span>
          <span class="xr-v">{Math.round(a.carbs ?? 0)}<small>g carbs</small></span>
          <span class="xr-s">{Math.round(a.fat ?? 0)} g fat</span>
        </div>
      {/if}
      {#if ctx.show('signature') && sig}
        <div class="xr-card">
          <span class="xr-k">Signature</span>
          <span class="xr-v">{sig.tp}<small>W TP</small></span>
          <span class="xr-s">{sig.hie} kJ HIE · {sig.pp} W PP</span>
        </div>
      {/if}
    </div>

    <div class="xr-chart">
      {#if ctx.show('chart') && ctx.data.series?.power}
        <Fill minHeight={110} let:width let:height>
          <PowerChart series={ctx.data.series} colored={ctx.options.colorPowerChart} showHr={ctx.options.chartHeartRate} hrColor="#a50013" {width} {height} showMpa={ctx.show('mpa')} breakthrough={ctx.data.breakthrough?.level ?? null} color="#1d2a30" mpaColor="var(--accent)" fillOpacity={0.2} />
        </Fill>
      {/if}
    </div>

    <div class="xr-stats"><StatGrid {stats} columns={4} size="sm" /></div>
    <div class="xr-badges"><Badges {ctx} own="activity" max={ctx.size === 'square' ? 2 : Infinity} /></div>
  </div>
</Frame>

<style>
  .xr-layout {
    flex: 1;
    min-height: 0;
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: auto auto auto auto minmax(0, 1fr) auto auto;
    grid-template-areas: 'top' 'title' 'xss' 'cards' 'chart' 'stats' 'badges';
    row-gap: 30px;
  }
  :global(.xr-square) .xr-layout { row-gap: 22px; }
  /* Landscape: what the ride was on the left, the ride itself on the right. */
  :global(.xr-landscape) .xr-layout {
    grid-template-columns: 620px minmax(0, 1fr);
    grid-template-rows: auto auto auto minmax(0, 1fr) auto auto;
    grid-template-areas:
      'top top'
      'title chart'
      'xss chart'
      'cards chart'
      'cards stats'
      'badges badges';
    column-gap: 70px;
    row-gap: 28px;
  }
  .xr-title { grid-area: title; min-width: 0; }
  .xr-xss { grid-area: xss; }
  .xr-cards { grid-area: cards; align-self: start; }
  .xr-chart { grid-area: chart; display: flex; flex-direction: column; min-height: 0; }
  .xr-stats { grid-area: stats; }
  .xr-badges { grid-area: badges; }
  .xr-badges:empty { display: none; }
  :global(.xr-square) .xr-xss-num { font-size: 116px; }
  :global(.xr-square) .xr-card { padding: 14px 20px; }
  :global(.xr-square) .xr-v { font-size: 46px; min-height: 46px; }
  :global(.xr-landscape) .xr-xss-num { font-size: 124px; }
  /* Landscape: the left column is narrow; the legend wraps and three cards get smaller numbers. */
  :global(.xr-landscape) .xr-legend { flex-wrap: wrap; justify-content: flex-start; gap: 6px 22px; }
  :global(.xr-landscape) .xr-card { padding: 16px 18px; }
  :global(.xr-landscape) .xr-v { font-size: 42px; min-height: 42px; }
  .xr-top {
    grid-area: top;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .xr-eyebrow {
    font-size: 24px;
    font-weight: 800;
    letter-spacing: 0.3em;
    text-transform: uppercase;
    color: var(--muted);
  }
  .xr-xss {
    display: flex;
    align-items: center;
    gap: 40px;
  }
  .xr-xss-total {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    line-height: 0.85;
  }
  .xr-xss-num {
    font-family: var(--display);
    font-weight: 800;
    font-size: 150px;
  }
  .xr-xss-label {
    font-size: 26px;
    font-weight: 800;
    letter-spacing: 0.3em;
    color: var(--muted);
  }
  .xr-xss-break {
    flex: 1;
    min-width: 0;
  }
  .xr-bar {
    display: flex;
    gap: 6px;
    height: 34px;
    border-radius: 999px;
    overflow: hidden;
  }
  .xr-legend {
    display: flex;
    justify-content: space-between;
    margin-top: 16px;
    font-size: 24px;
    color: var(--muted);
  }
  .xr-legend span {
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }
  .xr-legend i {
    width: 16px;
    height: 16px;
    border-radius: 50%;
  }
  .xr-legend strong {
    color: #11181c;
    font-weight: 800;
  }
  .xr-legend em {
    font-style: normal;
  }
  .xr-cards {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 16px;
  }
  .xr-card {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 20px 24px;
    border-radius: 20px;
    background: rgba(255, 255, 255, 0.7);
    border: 2px solid var(--faint);
    min-width: 0;
  }
  .xr-k {
    font-size: 19px;
    font-weight: 800;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--muted);
  }
  .xr-v {
    display: flex;
    align-items: baseline;
    min-height: 56px;
    align-items: center;
    font-family: var(--display);
    font-weight: 800;
    font-size: 56px;
    line-height: 1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .xr-v small {
    margin-left: 8px;
    font-size: 0.42em;
    color: var(--muted);
  }
  .xr-s {
    font-size: 22px;
    color: var(--muted);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
</style>
